import { type PropsWithChildren } from "react"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "~/components/reusable/hovercard/HoverCard"
import { SimilarityScoreBadge } from "~/components/forecast/nearestNeighbor/SimilarityScoreBadge"
import { Badge } from "~/components/reusable/Badge"
import { formatNumber } from "~/utils/formatting"
import { CellProductImage } from "~/components/planning/ProductTable/ProductCellRenderers"
import { cn, formatVariantId } from "~/utils/helpers"
import { Tooltip } from "~/components/reusable/Tooltip"
import { useVariantMap } from "~/hooks/useVariants"
import type { ContributingMatch } from "~/hooks/useCollectionComparisonTableData"
import type { VariantMatch } from "~/hooks/useVariantMatches"
import WeightedAverageFormulaSection from "./WeightedAverageFormulaSection"

type Props = PropsWithChildren & {
  matches: ContributingMatch[]
  allMatches?: VariantMatch[]
  getMatchValue?: (matchId: string) => number
  currentValue?: number
  comparisonValue?: number
  valueLabel?: string
}

export default function ComparisonMatchHoverCard({
  children,
  matches,
  allMatches,
  getMatchValue,
  currentValue,
  comparisonValue,
  valueLabel,
}: Props) {
  const variantMap = useVariantMap()

  const contributingMap = new Map(matches.map((m) => [m.match, m.similarity]))
  const displayMatches =
    allMatches ?? matches.map((m) => ({ match: m.match, combinedSimilarity: m.similarity }))

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <div className="flex items-center gap-1 w-full cursor-default justify-between">
          <div>{children}</div>
          <Badge variant="tag" size="sm" className="cursor-default shrink-0">
            Match
          </Badge>
        </div>
      </HoverCardTrigger>
      <HoverCardContent
        className="w-[400px] p-0 overflow-hidden divide-y divide-border-base"
        align="start"
        side="right"
        sideOffset={8}
        avoidCollisions={true}
      >
        <div className="px-3 py-2 bg-subtle flex items-center gap-2">
          <span className="label-base text-fg-base">Data from matched variant</span>
        </div>
        <div className="px-3 py-2 flex flex-col gap-2">
          {displayMatches.map(({ match, combinedSimilarity }) => {
            const isContributing = contributingMap.has(match)
            const similarity = contributingMap.get(match) ?? combinedSimilarity
            const variant = variantMap.get(match)
            const matchLabel = [variant?.productName, variant?.variantName]
              .filter(Boolean)
              .join(" · ")
            return (
              <Tooltip
                key={match}
                title={!isContributing ? "Not in comparison collection" : undefined}
                disabled={isContributing}
                side="left"
              >
                <div className={cn("flex items-center gap-3", !isContributing && "opacity-40")}>
                  <CellProductImage imageUrl={variant?.imageUrl ?? undefined} />
                  <div className="flex flex-col min-w-0 flex-1">
                    {matchLabel && (
                      <p className="paragraph-sm text-fg-base font-medium truncate">{matchLabel}</p>
                    )}
                    <p className="paragraph-sm text-fg-subtle font-mono">
                      #{formatVariantId(match)}
                    </p>
                  </div>
                  <SimilarityScoreBadge similarity={similarity} />
                  {getMatchValue !== undefined && (
                    <span className="paragraph-sm tabular-nums w-[50px] text-right font-medium text-fg-base">
                      {isContributing ? formatNumber(getMatchValue(match), 0) : "–"}
                    </span>
                  )}
                </div>
              </Tooltip>
            )
          })}
        </div>
        {comparisonValue !== undefined && (
          <div className="px-3 py-2 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="paragraph-sm text-fg-subtle">This variant</span>
              <span className="paragraph-sm font-medium text-fg-base">
                {formatNumber(currentValue ?? 0, 0)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="paragraph-sm text-fg-subtle">Weighted average</span>
              {getMatchValue !== undefined && comparisonValue !== undefined ? (
                <WeightedAverageFormulaSection
                  matches={matches}
                  getMatchValue={getMatchValue}
                  result={comparisonValue}
                  valueLabel={valueLabel}
                >
                  <span className="paragraph-sm font-medium text-fg-base">
                    {formatNumber(comparisonValue, 0)}
                  </span>
                </WeightedAverageFormulaSection>
              ) : (
                <span className="paragraph-sm font-medium text-fg-base">
                  {formatNumber(comparisonValue ?? 0, 0)}
                </span>
              )}
            </div>
          </div>
        )}
        <div className="px-3 py-2">
          <p className="paragraph-sm text-fg-subtle">
            {`This variant has no data in the comparison period. Numbers are a weighted average from ${matches.length} matched variant${matches.length === 1 ? "" : "s"} present in the comparison collection.`}
          </p>
        </div>
      </HoverCardContent>
    </HoverCard>
  )
}
